import React, { useEffect, useState } from 'react';
import { RootState, useAppDispatch, useAppSelector } from '@Store/store';
import { setSelectedIgAccount } from '@Store/igSelectionSlice';
import { useNavigate, useLocation } from 'react-router-dom';
import { ROUTES } from '@Constants/routes';
import { displayToast } from '@Utils/toast';
import { Avatar, LPIcon } from '../../assets/index';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@Components/shadcn/ui/pagination';
import { PlusCircleIcon } from '@heroicons/react/24/outline';

interface IGSelectProps {
  isModal?: boolean;
  onClose?: () => void;
}

const IGSelect: React.FC<IGSelectProps> = ({ isModal = false, onClose }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAppSelector((state: RootState) => state.profile);

  useEffect(() => {
    document.title = 'LinkPlease - Select Instagram Account';
  }, []);

  const igAccounts = user?.ig_ids || [];

  // Search state
  const [search, setSearch] = useState<string>('');
  const filteredAccounts = igAccounts.filter((ig: any) => (
    ig?.username?.toLowerCase().includes(search.toLowerCase())
  ));

  // Local pagination
  const [pageLimit, setPageLimit] = useState<number>(10);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const totalResults = filteredAccounts.length;
  const totalPages = Math.max(1, Math.ceil(totalResults / pageLimit));
  const startIndex = (pageNumber - 1) * pageLimit;
  const endIndex = Math.min(startIndex + pageLimit, totalResults);
  const visibleAccounts = filteredAccounts.slice(startIndex, endIndex);

  useEffect(() => {
    // Reset to page 1 when search or limit changes
    setPageNumber(1);
  }, [search, pageLimit]);

  const selectAccount = (ig: any) => {
    if (!ig?.igsid) return;

    dispatch(setSelectedIgAccount({ igId: ig.igsid, igData: ig }));
    displayToast('success', `Selected @${ig?.username}`);

    if (isModal && onClose) {
      onClose();
    } else {
      const from = (location.state as any)?.from || ROUTES.DASHBOARD;
      navigate(from);
    }
  };

  const handlePrev = () => setPageNumber((p) => Math.max(1, p - 1));
  const handleNext = () => setPageNumber((p) => Math.min(totalPages, p + 1));

  return (
    <div
      className="flex items-center justify-center p-6"
      style={{
        backgroundImage: 'url("/assests/bg.svg")',
        backgroundSize: 'cover',
        backgroundAttachment: 'fixed',
        minHeight: '100vh',
        width: '100%'
      }}
    >
      <div className="relative w-full max-w-md rounded-2xl border border-gray-200 bg-white shadow-lg">
        {/* Logo */}
        <img src={LPIcon} alt="LinkPlease" className="absolute -top-5 left-1/2 -translate-x-1/2 h-9 w-9" />

        {/* Search Bar */}
        <div className="px-4 pt-6 pb-2">
          <h1 className="sr-only">Select Instagram Account</h1>
          {igAccounts.length === 0 ? (
            <p className="text-center text-gray-600">No Instagram accounts connected.</p>
          ) : (
            <input
              type="text"
              placeholder="Search accounts"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full border rounded-md px-3 py-2 text-sm"
            />
          )}
        </div>

        {/* IG Accounts */}
        <div className="divide-y divide-gray-200 ">
          {visibleAccounts.map((ig: any) => (
            <button
              type="button"
              key={ig?.igsid}
              onClick={() => selectAccount(ig)}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 focus:bg-gray-50"
            >
              <img
                src={ig?.profile_picture_url || Avatar}
                alt={`@${ig?.username}`}
                className="h-8 w-8 rounded-md object-cover"
              />
              <span className="text-[15px] text-gray-800">{ig?.username}</span>
            </button>
          ))}
          {visibleAccounts.length === 0 && igAccounts.length > 0 && (
            <div className="px-4 py-6 text-center text-gray-500">No matches found</div>
          )}
        </div>

        {/* Pagination (ShadCN) — show only if results exceed current page size */}
        {totalResults > pageLimit && (
          <div className="px-4 py-3">
            <Pagination>
              <PaginationContent className="w-full flex items-center justify-between">
                <PaginationItem onClick={pageNumber === 1 ? undefined : () => handlePrev()}>
                  <PaginationPrevious className={pageNumber === 1 ? 'pointer-events-none opacity-50' : ''} />
                </PaginationItem>

                <PaginationItem>
                  <div className="flex items-center gap-3">
                    <PaginationLink isActive>{pageNumber}</PaginationLink>
                    <label className="flex items-center gap-2 text-sm">
                      <span>Page size</span>
                      <select
                        value={pageLimit}
                        onChange={(e) => setPageLimit(parseInt(e.target.value))}
                        className="border rounded-md px-2 py-1 text-sm"
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                    </label>
                  </div>
                </PaginationItem>

                <PaginationItem onClick={pageNumber === totalPages ? undefined : () => handleNext()}>
                  <PaginationNext className={pageNumber === totalPages ? 'pointer-events-none opacity-50' : ''} />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
            <p className="text-xs text-gray-600 mt-2">Total items in the page: {visibleAccounts.length}</p>
          </div>
        )}

        {/* Add Instagram button at bottom */}
        <div className="px-4 pb-4 mt-2 text-center">
          <button
            type="button"
            onClick={() => navigate(ROUTES.IG_CONNECT)}
            disabled={user?.is_enterprise && parseInt(user?.ent_acc_limit)<=igAccounts.length}
            className="w-full flex items-center gap-2 justify-center bg-indigo-600 hover:bg-indigo-700 text-white font-medium px-4 py-2 rounded-md disabled:bg-gray-300"
          >
            <PlusCircleIcon className="w-6 h-6" />
            Add Instagram
          </button>
          <p className='mt-2 text-gray-500'>
          {igAccounts.length} accounts / {parseInt(user?.ent_acc_limit)} accounts
          </p>
        </div>
      </div>
    </div>
  );
};

export default IGSelect;